'use client';

import useSWR, { mutate } from 'swr';
import { fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateManufactureList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/manufacture/search');
};

export const useManufactureDataList = (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue) => {
  const isFetchAll = page === undefined;
  const companies = (selectedCompanies || []).map((c) => c.value).join(',');

  const start = isFetchAll ? 0 : page * rowsPerPage;

  const payload = useMemo(() => {
    if (isFetchAll) return null;
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .like(searchKey, searchValue, 'AND')
      .addJoin('company', ['name'])
      .setOrder(['name'], true);

    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companies, isFetchAll]);

  const key = useMemo(() => ['/manufacture/search', JSON.stringify(payload)], [payload]);
  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  return {
    ...normalizeApiResponse(data, error, isLoading),
    refetch: () => mutate(key)
  };
};

export const getManufactureById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.get(`/manufacture/${id}${getTzParam()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch manufacture by ID:', error);
    throw error;
  }
};

export const createManufacture = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/manufacture/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateManufactureList();
    return response.data;
  } catch (error) {
    console.error('Error creating manufacture:', error);
    throw error;
  }
};

export const updateManufacture = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');

  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.put(`/manufacture/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateManufactureList();
    mutate(`/manufacture/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error updating manufacture:', error);
    throw error;
  }
};

export const deleteManufacture = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.delete(`/manufacture/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateManufactureList();
    return response.data;
  } catch (error) {
    console.error('Error deleting manufacture:', error);
    throw error;
  }
};
